import React, { useState, useEffect, useCallback, createContext, useContext, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import api from './api';
import { BranchProvider, useBranch } from './contexts/BranchContext';
import VersionUpdateManager from './components/VersionUpdateManager';
import PlanExpiryGate from './components/LogIns/PlanExpiryGate';
import { platformAccessState } from './utils/platformAccess';

const Header = React.lazy(() => import('./components/Header'));
const Sidebar = React.lazy(() => import('./components/Sidebar'));
const SignIn = React.lazy(() => import('./components/LogIns/SignInLanding'));
const PaymentStatus = React.lazy(() => import('./components/LogIns/PaymentStatus'));
const Admin = React.lazy(() => import('./pages/Admin'));
const IDCards = React.lazy(() => import('./pages/IDCards'));
const Seats = React.lazy(() => import('./pages/Seats'));
const Floor = React.lazy(() => import('./components/Floor'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Students = React.lazy(() => import('./pages/Students'));
const Bookings = React.lazy(() => import('./pages/Bookings'));
const FeePlans = React.lazy(() => import('./pages/FeePlans'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const Reminders = React.lazy(() => import('./pages/Reminders'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Expense = React.lazy(() => import('./pages/Expense'));
const ProfitLoss = React.lazy(() => import('./pages/ProfitLoss'));
const CheckIn = React.lazy(() => import('./pages/CheckIn'));
const CompleteProfile = React.lazy(() => import('./components/LogIns/CompleteProfile'));
const Books = React.lazy(() => import('./pages/Books'));
const Team = React.lazy(() => import('./pages/Team'));
const VideoTutorial = React.lazy(() => import('./pages/VideoTutorial'));
const Branches = React.lazy(() => import('./pages/Branches'));

// Public Pages - No Authentication Required
const ContactUs = React.lazy(() => import('./pages/ContectUs'));
const PrivacyPolicy = React.lazy(() => import('./pages/Privacypolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfServices'));
const DeleteAccount = React.lazy(() => import('./pages/DeleteAccount'));

const firebaseConfig = {
   apiKey: "AIzaSyBhyV-7A75rkHQ1ouDBtMWWn1PWFsIX1Fg",
  authDomain: "libmate-signin.firebaseapp.com",
  projectId: "libmate-signin",
  storageBucket: "libmate-signin.firebasestorage.app",
  messagingSenderId: "764163771306",
  appId: "1:764163771306:web:9eaf0ef8c6fa992643b9d3",
  measurementId: "G-SFQQQVXL4X"
};

const isLocalQaApi = () => /^http:\/\/(127\.0\.0\.1|localhost):(?:5055|5065)\/api\/?$/i.test(api.defaults.baseURL || '');
const hasLocalQaSession = () => isLocalQaApi()
    && localStorage.getItem('librarydesk.localQaSession') === 'true'
    && Boolean(localStorage.getItem('accessToken'));

if (!getApps().length) {
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    setPersistence(auth, browserLocalPersistence);
}

export const AuthContext = createContext(null);

const LoadingSpinner = () => (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="relative h-14 w-14">
            <div className="absolute inset-0 rounded-2xl bg-rose-100 animate-pulse"></div>
            <div className="absolute inset-2 animate-spin rounded-xl border-2 border-rose-200 border-t-rose-600"></div>
        </div>
    </div>
);

const AppContent = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [paymentStatusError, setPaymentStatusError] = useState('');
    // null until the first fetch resolves — see ProtectedRoute for why the
    // strict === true check matters.
    const [needsProfile, setNeedsProfile] = useState(null);
    const [backendUser, setBackendUser] = useState(null);
    const [mobileSidebar, setMobileSidebar] = useState(false);
    const [miniSidebar, setMiniSidebar] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    const navigate = useNavigate();

    /**
     * Has the owner filled in the "a few details" page yet?
     *
     * Read from GET /user/profile rather than the /users/auth response, because
     * the bootstrap below deliberately SKIPS that exchange when a JWT is already
     * in localStorage. Relying on the exchange would mean the page only ever
     * appeared on a cold first login and never again.
     */
    const checkProfile = useCallback(async () => {
        try {
            const { data } = await api.get('/user/profile');
            if (data?.success) {
                setBackendUser(data.user);
                setNeedsProfile(!data.user?.profileCompletedAt);
            }
        } catch (error) {
            // Never block the app on this. Worst case the owner is not asked.
            console.error('Profile check failed:', error);
            setNeedsProfile(false);
        }
    }, []);

    const checkPaymentStatus = useCallback(async () => {
        try {
            const response = await api.get('/user/payment-status');
            const hasPaid = response.data.PaymentStatus;
            setPaymentStatus(hasPaid);
            setPaymentDetails(response.data);
            setPaymentStatusError('');

            // Read the pathname at CALL time instead of depending on
            // location.pathname. With it in the deps, this callback got a new
            // identity on every navigation, the auth listener below re-ran, and
            // an unpaid account on /complete-profile entered an infinite
            // redirect ping-pong with ProtectedRoute — remounting the details
            // page continuously, so typing into it was impossible.
            const path = window.location.pathname;

            // /complete-profile is exempt on purpose: the profile gate comes
            // BEFORE the paywall (same order as ProtectedRoute). Shoving an
            // unpaid owner off the details page mid-typing was the other half
            // of that loop.
            if (!hasPaid && platformAccessState(response.data) === 'setup' && path !== '/payment-status' && path !== '/complete-profile') {
                navigate('/payment-status');
            }
        } catch (error) {
            console.error('Payment status check failed:', error);
            if (error.response?.status === 404) {
                // A local disposable database may have restarted, or the owner
                // account may have been removed. A retained JWT must not leave
                // the CRM on a permanent loading screen.
                localStorage.removeItem('accessToken');
                localStorage.removeItem('librarydesk.localQaSession');
                localStorage.removeItem('librarydesk.selectedBranchId');
                setUser(null);
                setPaymentStatus(null);
                setPaymentDetails(null);
                setBackendUser(null);
                setNeedsProfile(null);
                navigate('/signin', { replace: true });
            } else if (error.response?.status === 403) {
                // This endpoint has no plan-level permission check: a 403 means
                // the backend rejected our session even after the API refresh
                // interceptor had a chance to repair it. Return to sign-in
                // rather than presenting an unauthenticated owner with a
                // misleading connection error.
                localStorage.removeItem('accessToken');
                localStorage.removeItem('librarydesk.localQaSession');
                localStorage.removeItem('librarydesk.selectedBranchId');
                try { await signOut(getAuth()); } catch (_) { /* continue clearing state */ }
                setUser(null);
                setPaymentStatus(null);
                setPaymentDetails(null);
                setPaymentStatusError('');
                setBackendUser(null);
                setNeedsProfile(null);
                navigate('/signin', { replace: true });
            } else if (error.response?.status !== 401) {
                setPaymentStatusError(error.response
                    ? 'The server could not check your plan. Please retry.'
                    : 'Could not reach the server. Check the connection and retry.');
            }
        }
    }, [navigate]);

    const handleLogout = async () => {
        try {
            const auth = getAuth();
            await signOut(auth);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('librarydesk.localQaSession');
            setUser(null);
            setPaymentStatus(null);
            setPaymentDetails(null);
            navigate('/signin');
        } catch (error) {
            console.error('Logout error:', error);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('librarydesk.localQaSession');
            setUser(null);
            setPaymentStatus(null);
            setPaymentDetails(null);
            navigate('/signin');
        }
    };

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            const existingToken = localStorage.getItem('accessToken');
            if (currentUser) {

                if (existingToken) {
                    // JWT exists — skip backend re-auth, just use it
                    setUser(currentUser);
                    await Promise.all([checkPaymentStatus(), checkProfile()]);
                } else {
                    // No JWT — first login, authenticate with backend
                    try {
                        const idToken = await currentUser.getIdToken();
                        const response = await api.post('/users/auth', { idToken });
                        localStorage.setItem('accessToken', response.data.accessToken);
                        setUser(currentUser);
                        await Promise.all([checkPaymentStatus(), checkProfile()]);
                    } catch (error) {
                        console.error('Backend authentication error:', error);
                        // Only logout on definitive auth failures, not network/rate-limit errors
                        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                            handleLogout();
                        }
                    }
                }
            } else {
                if (hasLocalQaSession()) {
                    // Local QA uses a backend-issued JWT without creating a
                    // Firebase identity. Keep this path impossible on live APIs.
                    setUser({ uid: 'local-qa-owner', displayName: 'LibraryDesk QA Tester', email: 'qa-owner@librarydesk.test' });
                    await Promise.all([checkPaymentStatus(), checkProfile()]);
                } else {
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('librarydesk.localQaSession');
                    setUser(null);
                    setPaymentStatus(null);
                    setPaymentDetails(null);
                    setNeedsProfile(null);
                    setBackendUser(null);
                }
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [checkPaymentStatus, checkProfile]);

    useEffect(() => {
        if (user) {
            const intervalId = setInterval(() => {
                checkPaymentStatus();
            }, 300000);
            return () => clearInterval(intervalId);
        }
    }, [user, checkPaymentStatus]);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (mobileSidebar && !event.target.closest('.mobile-sidebar') && !event.target.closest('.mobile-menu-button')) {
          setMobileSidebar(false);
        }
      };
      const handleResize = () => {
        if (window.innerWidth >= 768) { setMobileSidebar(false); }
      };
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleResize);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('resize', handleResize);
      };
    }, [mobileSidebar]);

    if (loading) {
        return <LoadingSpinner />;
    }

    const authContextValue = {
        user,
        loading,
        paymentStatus,
        paymentDetails,
        paymentStatusError,
        retryPaymentStatus: checkPaymentStatus,
        layoutProps: { mobileSidebar, setMobileSidebar, miniSidebar, setMiniSidebar, expandedItems, setExpandedItems },
        needsProfile,
        backendUser,
        // Called by CompleteProfile once the owner has answered, so the gate
        // opens without a full reload.
        onProfileComplete: async () => { setNeedsProfile(false); await checkProfile(); },
        logout: handleLogout
    };

    const layoutProps = {
        mobileSidebar,
        setMobileSidebar,
        miniSidebar,
        setMiniSidebar,
        expandedItems,
        setExpandedItems,
    };

    return (
        <AuthContext.Provider value={authContextValue}>
            <Routes>
                {/* Public Routes - No Authentication Required */}
                <Route path="/contact-us" element={
                    <Suspense fallback={<LoadingSpinner />}><ContactUs /></Suspense>
                } />
                <Route path="/privacy-policy" element={
                    <Suspense fallback={<LoadingSpinner />}><PrivacyPolicy /></Suspense>
                } />
                <Route path="/terms-of-service" element={
                    <Suspense fallback={<LoadingSpinner />}><TermsOfService /></Suspense>
                } />
                <Route path="/delete-account" element={
                    <Suspense fallback={<LoadingSpinner />}><DeleteAccount /></Suspense>
                } />

                {/* Auth Routes */}
                <Route path="/signin" element={
                    user ? <Navigate to="/dashboard" replace /> : <Suspense fallback={<LoadingSpinner />}><SignIn /></Suspense>
                } />
                <Route path="/complete-profile" element={
                    <ProtectedRoute>
                        <Suspense fallback={<LoadingSpinner />}>
                            <CompleteProfileRoute />
                        </Suspense>
                    </ProtectedRoute>
                } />
                <Route path="/payment-status" element={
                    <ProtectedRoute>
                        <Suspense fallback={<LoadingSpinner />}><PaymentStatus /></Suspense>
                    </ProtectedRoute>
                } />

                {/* Protected Routes */}
                <Route path="/*" element={
                    <ProtectedRoute>
                        <BranchProvider><Layout {...layoutProps}>
                            <Routes>
                                <Route path="dashboard" element={<Suspense fallback={<LoadingSpinner />}><Dashboard /></Suspense>} />
                                <Route path="admin" element={<Suspense fallback={<LoadingSpinner />}><Admin /></Suspense>} />
                                <Route path="floors" element={<Suspense fallback={<LoadingSpinner />}><Floor /></Suspense>} />
                                <Route path="id-cards" element={<Suspense fallback={<LoadingSpinner />}><IDCards /></Suspense>} />
                                <Route path="seats" element={<Suspense fallback={<LoadingSpinner />}><Seats /></Suspense>} />
                                <Route path="students" element={<Suspense fallback={<LoadingSpinner />}><Students /></Suspense>} />
                                <Route path="bookings" element={<Suspense fallback={<LoadingSpinner />}><Bookings /></Suspense>} />
                                <Route path="fee-plans" element={<Suspense fallback={<LoadingSpinner />}><FeePlans /></Suspense>} />
                                <Route path="notifications" element={<Suspense fallback={<LoadingSpinner />}><Notifications /></Suspense>} />
                                <Route path="reminders" element={<Suspense fallback={<LoadingSpinner />}><Reminders /></Suspense>} />
                                <Route path="reports" element={<Suspense fallback={<LoadingSpinner />}><Reports /></Suspense>} />
                                <Route path="expenses" element={<Suspense fallback={<LoadingSpinner />}><Expense /></Suspense>} />
                                <Route path="profit-loss" element={<Suspense fallback={<LoadingSpinner />}><ProfitLoss /></Suspense>} />
                                <Route path="check-in" element={<Suspense fallback={<LoadingSpinner />}><CheckIn /></Suspense>} />
                                <Route path="books" element={<Suspense fallback={<LoadingSpinner />}><Books /></Suspense>} />
                                <Route path="team" element={<Suspense fallback={<LoadingSpinner />}><Team /></Suspense>} />
                                <Route path="branches" element={<Suspense fallback={<LoadingSpinner />}><Branches /></Suspense>} />
                                <Route path="video-tutorial" element={<Suspense fallback={<LoadingSpinner />}><VideoTutorial /></Suspense>} />
                                <Route index element={<Navigate to="dashboard" replace />} />
                                <Route path="*" element={<Navigate to="dashboard" replace />} />
                            </Routes>
                        </Layout></BranchProvider>
                    </ProtectedRoute>
                } />
            </Routes>
        </AuthContext.Provider>
    );
};

export const ProtectedRoute = ({ children }) => {
    const { user, loading, paymentStatus, paymentDetails, paymentStatusError, retryPaymentStatus, needsProfile, backendUser, layoutProps } = useContext(AuthContext);
    const location = useLocation();
    if (loading) { return <LoadingSpinner />; }
    if (!user) { return <Navigate to="/signin" state={{ from: location }} replace />; }
    // Ordered before the paywall on purpose: a new owner should be able to give
    // their name and link a second sign-in method before being asked to pay.
    // `=== true`, never truthy — needsProfile is null until the first fetch
    // resolves, and a truthy check would flash this page on every load.
    if (needsProfile === true && location.pathname !== '/complete-profile') { return <Navigate to="/complete-profile" replace />; }
    if (paymentStatus == null) return paymentStatusError
        ? <main className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center" role="alert"><p>{paymentStatusError}</p><button type="button" className="billing-primary" onClick={retryPaymentStatus}>Retry</button></main>
        : <LoadingSpinner />;
    if (paymentStatus === false && location.pathname !== '/payment-status' && location.pathname !== '/complete-profile') {
        if (platformAccessState(paymentDetails) === 'setup') return <Navigate to="/payment-status" replace />;
        return <BranchProvider><Layout {...layoutProps} preserveContent><PlanExpiryGate details={paymentDetails} libraryName={backendUser?.libraryName}/></Layout></BranchProvider>;
    }
    return <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>;
};

/** Thin wrapper so the lazy route can pull its props off the context. */
export const CompleteProfileRoute = () => {
    const { backendUser, onProfileComplete } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const reviewFromPayment = new URLSearchParams(location.search).get('from') === 'payment';
    return (
        <CompleteProfile
            user={backendUser}
            reviewFromPayment={reviewFromPayment}
            onDone={async () => {
                await onProfileComplete?.();
                navigate(reviewFromPayment ? '/payment-status' : '/dashboard', { replace: true });
            }}
        />
    );
};

const Layout = ({ children, preserveContent = false, ...props }) => {
    const { selectedBranchId } = useBranch();
    return (
    <div className="crm-shell min-h-screen text-slate-900"><a className="workspace-skip-link" href="#main-content">Skip to content</a>
        <Suspense fallback={<LoadingSpinner />}>
            <Header {...props} />
            <Sidebar {...props} />
            {props.mobileSidebar && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={() => props.setMobileSidebar(false)}
                />
            )}
            <main id="main-content" tabIndex={-1} className={`crm-main transition-all duration-300 ease-out pt-16 ${props.miniSidebar ? 'md:ml-20' : 'md:ml-60'} min-h-screen`}>
                <div key={preserveContent ? 'plan-access' : selectedBranchId || 'branch-loading'} className="crm-content mx-auto w-full max-w-[1680px] p-4 sm:p-6 lg:p-8">{children}</div>
            </main>
        </Suspense>
    </div>
    );
};

const App = () => (
    <Router>
        <VersionUpdateManager />
        <AppContent />
    </Router>
);

export default App;
